// Copyright (c) 2021 WiseTime. All rights reserved.

import { concat, Observable, of } from "rxjs"
import { Action } from "redux"
import { catchError, filter, mergeMap } from "rxjs/operators"
import * as TagsSlice from "../slice"
import { TagId } from "../slice"
import * as TagActions from "../actions"
import { Tag } from "generated/legebill/type/tag_pb"
import { TagUpdate, UpdateTagRequest } from "generated/legebill/site/tag/v4/tag_pb"
import { grpc } from "../../../app/rpc/executor"
import { tagServiceClient } from "../../../app/rpc/client"
import { isGrpcError } from "../../../lib/rpcutils"
import { StatusCode } from "grpc-web"
import { t } from "@lingui/macro"
import { showSnackBar } from "../../snackbar/actions"

type UpdateTag = (request: UpdateTagRequest) => Observable<TagUpdate>

export const updateTag = (updateTag: UpdateTag) =>
  (action$: Observable<Action>): Observable<Action> =>
    action$.pipe(
      filter(TagActions.updateTag.match),
      mergeMap(({ payload }) => of(payload)
        .pipe(
          mergeMap(updateTag),
          mergeMap(result => concat(
            of(TagsSlice.updateTag({
              tagId: payload.getId(),
              name: result.getName(),
              description: result.getDescription(),
              suggestionConfig: result.getSuggestionConfig(),
            })),
            of(TagsSlice.setDialogStatus("CLOSED")),
          )),
          catchError(e => handleError(e, payload.getId()))
        ),
      )
    )

const updateTagRpc = (request: UpdateTagRequest): Observable<Tag> =>
  grpc(() => tagServiceClient.updateTag(request, null))

const handleError = (e: unknown, tagId: TagId) => {
  if (isGrpcError(e)) {
    const { code } = e
    if (code === StatusCode.NOT_FOUND) {
      return concat(
        of(showSnackBar({
          message: t({
            id: "msg_get_tag_not_found",
            message: "Tag not found. Please try again in a couple of minutes. If the error persists, please contact support@wisetime.com."
          }),
          messageType: "ERROR",
        })),
        of(TagsSlice.setDialogStatus("CLOSED")),
        of(TagsSlice.removeFromCache(tagId))
      )
    }

    if (code === StatusCode.PERMISSION_DENIED) {
      return concat(
        of(showSnackBar({
          message: t({
            id: "msg_update_tag_rpc_permission_denied",
            message: "Failed to edit tag. You do not have permission to edit tags."
          }),
          messageType: "ERROR"
        })),
        of(TagsSlice.setDialogStatus("CLOSED"))
      )
    }

    if (code === StatusCode.ALREADY_EXISTS) {
      return concat(
        of(showSnackBar({
          message: t({
            id: "msg_update_tag_rpc_already_exists",
            message: "Failed to edit tag. Tag with same name already exists."
          }),
          messageType: "ERROR"
        })),
        of(TagsSlice.setDialogStatus("CLOSED"))
      )
    }
  }

  return concat(
    of(showSnackBar({
      message: t({
        id: "msg_update_tag_rpc_failed",
        message: "Failed to update tag. Please try again in a couple of minutes. If the error persists, please contact support@wisetime.com."
      }),
      messageType: "ERROR",
    })),
    of(TagsSlice.setDialogStatus("ERROR")),
  )
}

export default updateTag(updateTagRpc)
